/**
 * #1986 — a sidebar click must always land on the page's landing state.
 *
 * The hubs keep their sub-view in `?tab=` (and the default tab DELETES the
 * param, #1702), and pages keep filters/toggles in local React state. So a
 * sidebar item whose path equals the current path produced no route change
 * and no remount: clicking "Finished Goods" while on /quality was a no-op and
 * there was no way back to the landing tab.
 *
 * Mechanism: every sidebar link carries a fresh reset nonce in
 * `location.state`; the shell keys the routed page on the last nonce it saw,
 * so a sidebar click remounts the page (fresh local state, clean search
 * params) while in-page navigation (tab switches, filters, deep links) —
 * which never carries the nonce — leaves the mount untouched.
 */
export const NAV_RESET_KEY = "navReset";

/** Location state a sidebar link navigates with. A new nonce per call. */
export function navResetState(nonce: number = Date.now()): Record<string, number> {
  return { [NAV_RESET_KEY]: nonce };
}

/** The nonce carried by a location state, or null when the navigation
 *  did not come from the sidebar (tab switch, deep link, back button). */
export function readNavReset(state: unknown): number | null {
  if (!state || typeof state !== "object") return null;
  const v = (state as Record<string, unknown>)[NAV_RESET_KEY];
  return typeof v === "number" ? v : null;
}

/**
 * The React key for the routed page. Changes ONLY when a sidebar click lands
 * with a nonce the shell has not seen — never on in-page navigation, so a
 * hub's tab switch or a filter change does not throw the page away.
 */
export function nextPageMountKey(prev: number, state: unknown): number {
  const nonce = readNavReset(state);
  return nonce !== null && nonce !== prev ? nonce : prev;
}
